import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import {
  closeActiveSession,
  dockSessions as selectDock,
  focusSession,
  initialCustomerChatState,
  minimizeChat,
  openSession,
  setRightTab as applyRightTab,
  type CustomerChatRightTab,
  type CustomerChatState,
} from "@/lib/customer-chat-state";
import type { WecomExternalContact } from "@/lib/wecom-external-contacts";

type ContactLite = Pick<WecomExternalContact, "id" | "name" | "avatar_url">;

type CustomerChatContextValue = {
  state: CustomerChatState;
  isFullscreen: boolean;
  isMinimized: boolean;
  openCustomerChat: (contact: ContactLite) => void;
  switchContact: (contact: ContactLite) => void;
  minimize: () => void;
  closeActive: () => void;
  focusContact: (contactId: number) => void;
  setRightTab: (tab: CustomerChatRightTab) => void;
  dockSessions: ReturnType<typeof selectDock>;
};

const CustomerChatContext = createContext<CustomerChatContextValue | null>(null);

function contactToSession(contact: ContactLite) {
  return {
    contactId: contact.id,
    name: contact.name,
    avatarUrl: contact.avatar_url,
  };
}

export function CustomerChatProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(() => initialCustomerChatState());

  const openCustomerChat = useCallback((contact: ContactLite) => {
    setState((prev) => openSession(prev, contactToSession(contact)));
  }, []);

  const switchContact = useCallback((contact: ContactLite) => {
    setState((prev) => openSession(prev, contactToSession(contact)));
  }, []);

  const minimize = useCallback(() => {
    setState((prev) => minimizeChat(prev));
  }, []);

  const closeActive = useCallback(() => {
    setState((prev) => closeActiveSession(prev));
  }, []);

  const focusContact = useCallback((contactId: number) => {
    setState((prev) => focusSession(prev, contactId));
  }, []);

  const setRightTab = useCallback((tab: CustomerChatRightTab) => {
    setState((prev) => applyRightTab(prev, tab));
  }, []);

  const value = useMemo(
    () => ({
      state,
      isFullscreen: state.mode === "fullscreen",
      isMinimized: state.mode === "minimized",
      openCustomerChat,
      switchContact,
      minimize,
      closeActive,
      focusContact,
      setRightTab,
      dockSessions: selectDock(state),
    }),
    [state, openCustomerChat, switchContact, minimize, closeActive, focusContact, setRightTab],
  );

  return <CustomerChatContext.Provider value={value}>{children}</CustomerChatContext.Provider>;
}

export function useCustomerChat() {
  const ctx = useContext(CustomerChatContext);
  if (!ctx) throw new Error("useCustomerChat must be used within CustomerChatProvider");
  return ctx;
}
